import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { StatusDot } from '@/components/ui/custom/StatusDot';
import { cn } from '@/lib/utils';
import { Integration } from '@/shared/types/integration';
import { IntegrationStatusDisplay, getIntegrationStatusDisplay } from '@/util/integrationStatus';

const variantTextColor: Record<IntegrationStatusDisplay['variant'], string> = {
  successful: 'text-success',
  neutral: 'text-muted-foreground',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

interface IntegrationStatusProps {
  integration: Pick<Integration, 'status' | 'active'>;
}

/**
 * IntegrationStatus - Component for displaying integration status
 *
 * Shows the current status of a connected integration with a colored dot
 * and status text. Supports multiple status states including active, disabled,
 * verifying, pending setup, missing credentials, and error states.
 *
 * @param {Pick<Integration, 'status' | 'active'>} integration - Integration object containing status and active state
 * @returns {JSX.Element} The integration status component
 */
const IntegrationStatus = ({ integration }: IntegrationStatusProps) => {
  const { t } = useTranslation();
  const { label, variant, tooltip } = getIntegrationStatusDisplay(integration, t);

  const badge = (
    <div className="flex items-center gap-1">
      <StatusDot aria-hidden="true" variant={variant} />
      <span className={cn('text-xs leading-4 font-normal', variantTextColor[variant])}>
        {label}
      </span>
    </div>
  );

  if (!tooltip) {
    return badge;
  }

  return (
    <InfoTooltip content={tooltip} size="large" asChild={false}>
      {badge}
    </InfoTooltip>
  );
};

export default IntegrationStatus;
